package com.kinoshazam.di

import com.kinoshazam.Constants
import com.kinoshazam.Constants.IS_LOCAL_TEST
import com.kinoshazam.Constants.URL_PATH
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.data.PendingAuthCredentials
import com.kinoshazam.data.TokenRepository
import com.kinoshazam.resolveApiHost
import io.github.aakira.napier.DebugAntilog
import io.github.aakira.napier.Napier
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import org.koin.core.qualifier.named
import org.koin.dsl.module

const val AUTH_USER = "AUTH_USER"
const val NONE_AUTH_USER = "NONE_AUTH_USER"

fun networkModule(
    enableNetworkLogs: Boolean,
) = module {
    single {
        TokenRepository(
            storageUser = get(named(STORAGE_USER)),
            storageSecure = get(named(STORAGE_SECURE)),
            httpClient = get(named(NONE_AUTH_USER)),
            authHttpClient = lazy { get(named(AUTH_USER)) },
            globalStateRepository = get()
        )
    }
    single { PendingAuthCredentials() }
    single(named(AUTH_USER)) {
        provideHttpClient(
            enableNetworkLogs = enableNetworkLogs,
            tokenRepository = lazy { get<TokenRepository>() },
            globalStateRepository = get(),
            isAuth = true
        )
    }
    single(named(NONE_AUTH_USER)) {
        provideHttpClient(
            enableNetworkLogs = enableNetworkLogs,
            tokenRepository = lazy { get<TokenRepository>() },
            globalStateRepository = get(),
            isAuth = false,
        )
    }
}

private fun provideHttpClient(
    enableNetworkLogs: Boolean,
    tokenRepository: Lazy<TokenRepository>,
    globalStateRepository: GlobalStateRepository,
    isAuth: Boolean,
): HttpClient {
    return HttpClient {
        install(HttpTimeout) {
            requestTimeoutMillis = 60_000
            connectTimeoutMillis = 20_000
            socketTimeoutMillis = 60_000
        }
        expectSuccess = true
        addDefaultResponseValidation()

        if (isAuth) {
            install(Auth) {
                bearer {
                    loadTokens {
                        val access = tokenRepository.value.getAccessToken()
                        val refresh = tokenRepository.value.getRefreshToken()
                        if (access != null && refresh != null) {
                            BearerTokens(access, refresh)
                        } else {
                            null
                        }
                    }

                    refreshTokens {
                        tokenRepository.value.refresh()
                    }
                }
            }
        }

        defaultRequest {
            contentType(ContentType.Application.Json)
            url {
                if (IS_LOCAL_TEST) {
                    protocol = URLProtocol.HTTP
                    host = resolveApiHost()
                    port = Constants.LOCAL_PORT
                } else {
                    protocol = URLProtocol.HTTPS
                    host = resolveApiHost()
                }
                path(URL_PATH)
            }
            headers {
                append("accept", "application/json")
                append("X-platform", globalStateRepository.platformConfig.platform.wireName)
                append("X-appVersion", globalStateRepository.platformConfig.version)
                append("X-deviceId", globalStateRepository.deviceId)
                append("X-locale", globalStateRepository.appLanguage.value.locale)
            }
        }

        if (enableNetworkLogs || Constants.ENABLE_NETWORK_LOGS) {
            install(Logging) {
                level = LogLevel.ALL
                logger = object : Logger {
                    override fun log(message: String) {
                        Napier.i(tag = "Http Client", message = message)
                    }
                }
            }.also {
                Napier.base(DebugAntilog())
            }
        }

        install(ContentNegotiation) {
            json(
                Json {
                    ignoreUnknownKeys = true
                    isLenient = true
                }
            )
        }
    }
}
